<template>
  <div>
    <div class="toolbar">
      <div class="toolbar-group">
        <BFormSelect
          v-model="headingLevel"
          size="sm"
          class="heading-select"
          @change="setHeading"
        >
          <option :value="0">
            Text
          </option>
          <option :value="1">
            {{ $t('posts.editor.heading') }} 1
          </option>
          <option :value="2">
            {{ $t('posts.editor.heading') }} 2
          </option>
          <option :value="3">
            {{ $t('posts.editor.heading') }} 3
          </option>
        </BFormSelect>
      </div>

      <div class="toolbar-separator" />

      <button
        class="toolbar-btn"
        :class="{ active: editorActive('bold') }"
        type="button"
        @click="editorCommand('bold')"
      >
        <i class="fas fa-bold" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive('italic') }"
        type="button"
        @click="editorCommand('italic')"
      >
        <i class="fas fa-italic" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive('underline') }"
        type="button"
        @click="editorCommand('underline')"
      >
        <i class="fas fa-underline" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive('strike') }"
        type="button"
        @click="editorCommand('strike')"
      >
        <i class="fas fa-strikethrough" />
      </button>

      <div class="toolbar-separator" />

      <button
        class="toolbar-btn"
        :class="{ active: editorActive('bulletList') }"
        type="button"
        @click="editorCommand('bulletList')"
      >
        <i class="fas fa-list-ul" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive('orderedList') }"
        type="button"
        @click="editorCommand('orderedList')"
      >
        <i class="fas fa-list-ol" />
      </button>

      <div class="toolbar-separator" />

      <button
        class="toolbar-btn"
        :class="{ active: editorActive({ textAlign: 'left' }) }"
        type="button"
        @click="editorCommand('alignLeft')"
      >
        <i class="fas fa-align-left" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive({ textAlign: 'center' }) }"
        type="button"
        @click="editorCommand('alignCenter')"
      >
        <i class="fas fa-align-center" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive({ textAlign: 'right' }) }"
        type="button"
        @click="editorCommand('alignRight')"
      >
        <i class="fas fa-align-right" />
      </button>

      <div class="toolbar-separator" />

      <button
        class="toolbar-btn"
        :class="{ active: editorActive('blockquote') }"
        type="button"
        @click="editorCommand('blockquote')"
      >
        <i class="fas fa-quote-left" />
      </button>
      <button
        class="toolbar-btn"
        :class="{ active: editorActive('codeBlock') }"
        type="button"
        @click="editorCommand('codeBlock')"
      >
        <i class="fas fa-code" />
      </button>
      <button
        class="toolbar-btn"
        type="button"
        @click="editorCommand('horizontalRule')"
      >
        <i class="fas fa-minus" />
      </button>

      <template v-if="enableLinksAndImages">
        <div class="toolbar-separator" />

        <button
          class="toolbar-btn"
          :class="{ active: editorActive('link') }"
          type="button"
          @click="toggleLink"
        >
          <i class="fas fa-link" />
        </button>
        <button
          class="toolbar-btn"
          type="button"
          @click="insertImage"
        >
          <i class="fas fa-image" />
        </button>
      </template>

      <div class="toolbar-separator" />

      <button
        class="toolbar-btn"
        type="button"
        @click="editorCommand('undo')"
      >
        <i class="fas fa-undo" />
      </button>
      <button
        class="toolbar-btn"
        type="button"
        @click="editorCommand('redo')"
      >
        <i class="fas fa-redo" />
      </button>
    </div>

    <EditorContent
      :editor="editor"
      class="editor-content"
      :style="editorStyle"
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';

export default {
  name: 'RichTextEditor',
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    minHeight: {
      type: Number,
      default: 200,
    },
    maxHeight: {
      type: Number,
      default: 500,
    },
    enableLinksAndImages: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editor: null,
      headingLevel: 0,
    };
  },
  components: {
    EditorContent,
  },
  computed: {
    editorStyle() {
      return {
        minHeight: `${this.minHeight}px`,
        maxHeight: `${this.maxHeight}px`,
      };
    },
  },
  watch: {
    value(nextValue) {
      if (!this.editor) return;
      const next = nextValue || '';
      const current = this.editor.getHTML();
      if (current === next) return;
      this.editor.commands.setContent(next, false);
      this.syncHeading();
    },
  },
  methods: {
    syncHeading() {
      if (!this.editor) return;
      if (this.editor.isActive('heading', { level: 1 })) {
        this.headingLevel = 1;
      } else if (this.editor.isActive('heading', { level: 2 })) {
        this.headingLevel = 2;
      } else if (this.editor.isActive('heading', { level: 3 })) {
        this.headingLevel = 3;
      } else {
        this.headingLevel = 0;
      }
    },
    editorActive(check) {
      if (!this.editor) return false;
      return this.editor.isActive(check);
    },
    editorCommand(cmd) {
      if (!this.editor) return;
      const chain = this.editor.chain().focus();
      switch (cmd) {
        case 'bold': chain.toggleBold().run(); break;
        case 'italic': chain.toggleItalic().run(); break;
        case 'underline': chain.toggleUnderline().run(); break;
        case 'strike': chain.toggleStrike().run(); break;
        case 'bulletList': chain.toggleBulletList().run(); break;
        case 'orderedList': chain.toggleOrderedList().run(); break;
        case 'alignLeft': chain.setTextAlign('left').run(); break;
        case 'alignCenter': chain.setTextAlign('center').run(); break;
        case 'alignRight': chain.setTextAlign('right').run(); break;
        case 'blockquote': chain.toggleBlockquote().run(); break;
        case 'codeBlock': chain.toggleCodeBlock().run(); break;
        case 'horizontalRule': chain.setHorizontalRule().run(); break;
        case 'undo': chain.undo().run(); break;
        case 'redo': chain.redo().run(); break;
        default: break;
      }
    },
    setHeading(level) {
      if (!this.editor) return;
      if (level === 0) {
        this.editor.chain().focus().setParagraph().run();
      } else {
        this.editor.chain().focus().toggleHeading({ level }).run();
      }
    },
    toggleLink() {
      if (!this.editor) return;

      if (this.editor.isActive('link')) {
        if (typeof this.editor.chain().focus().unsetLink === 'function') {
          this.editor.chain().focus().unsetLink().run();
        }
        return;
      }

      // eslint-disable-next-line no-alert
      const url = window.prompt(this.$t('posts.editor.enterUrl'));
      if (url && typeof this.editor.chain().focus().setLink === 'function') {
        this.editor.chain().focus().setLink({ href: url }).run();
      }
    },
    insertImage() {
      // eslint-disable-next-line no-alert
      const url = window.prompt(this.$t('posts.editor.enterImageUrl'));
      if (url && this.editor && typeof this.editor.chain().focus().setImage === 'function') {
        this.editor.chain().focus().setImage({ src: url }).run();
      }
    },
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      content: this.value || '',
      onUpdate: ({ editor }) => {
        this.syncHeading();
        this.$emit('input', editor.getHTML());
      },
    });

    this.syncHeading();
  },
  beforeDestroy() {
    if (this.editor && !this.editor.isDestroyed) {
      try {
        this.editor.destroy();
      } catch {
        // editor view may not be available yet
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/vars';

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: #f8f9fa;
}

.toolbar-group {
  display: flex;
  align-items: center;
}

.heading-select {
  width: 130px;
  font-size: 13px;
  border-radius: 4px;
  height: 32px;
  padding: 2px 8px;
}

.toolbar-btn {
  border: none;
  background: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms;

  &:hover {
    background-color: #e0e0e0;
  }

  &.active {
    background-color: rgba($blue, 0.15);
    color: $darkblue;
  }
}

.toolbar-separator {
  width: 1px;
  height: 20px;
  background: #ddd;
  margin: 0 4px;
}

.editor-content {
  border: 1px solid #e0e0e0;
  border-radius: 0 0 8px 8px;
  overflow-y: auto;

  ::v-deep .ProseMirror {
    padding: 12px 16px;
    min-height: inherit;
    outline: none;

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }

    h1 {
      font-size: 1.8em;
      font-weight: 700;
    }

    h2 {
      font-size: 1.4em;
      font-weight: 600;
    }

    h3 {
      font-size: 1.15em;
      font-weight: 600;
    }

    ul, ol {
      padding-left: 1.5em;
    }

    p {
      margin: 0 0 0.4em;
    }

    blockquote {
      border-left: 3px solid #ddd;
      padding-left: 12px;
      color: #666;
      margin: 0.6em 0;
    }

    pre {
      background: #f4f4f4;
      border-radius: 6px;
      padding: 10px 14px;
      font-size: 13px;
    }

    hr {
      border: none;
      border-top: 2px solid #e0e0e0;
      margin: 1em 0;
    }

    a {
      color: $blue;
      text-decoration: underline;
      cursor: pointer;
    }

    img {
      max-width: 100%;
      border-radius: 6px;
      margin: 0.5em 0;
    }
  }
}
</style>
